import { Dispatch, SetStateAction, useCallback } from 'react';

import { isPast, secondsToMilliseconds } from 'date-fns';

import { StreamTypes } from 'store/player/actions.types';
import { RBChannel } from 'store/railsBuilder/reducer.types';

import parseVideoItem from 'services/data-manager/parsing/parseVideoItem';
import { VideoItem } from 'services/data-manager/types';

import useGetLiveProgram from 'utils/hooks/useGetLiveProgram';
import { isRecordingItem } from 'utils/recordings';

import { SwitchPlayerSource } from 'screens/PlayerModal/types';

type Params = {
    channel: RBChannel | undefined;
    videoItem: VideoItem | undefined;
    onSwitchTimeshiftModeOff(): void;
    onResetTimeshiftNotification(): void;
    onResetTimeshiftNotificationShown(): void;
    onSwitchPlayerSource: SwitchPlayerSource;
    setPlayerPaused: Dispatch<SetStateAction<boolean>>;
    setPlayerPausedByUser: Dispatch<SetStateAction<boolean>>;
    onSetReadyToPlay(readyToPlay: boolean): void;
    onUpdateBookmark(): void;
};

function useJumpToLive({
    channel,
    videoItem,
    onSwitchTimeshiftModeOff,
    onResetTimeshiftNotification,
    onResetTimeshiftNotificationShown,
    onSwitchPlayerSource,
    setPlayerPaused,
    setPlayerPausedByUser,
    onSetReadyToPlay,
    onUpdateBookmark,
}: Params) {
    const onGetLiveProgram = useGetLiveProgram();

    return useCallback(async () => {
        if (channel && videoItem) {
            onSetReadyToPlay(false);
            onSwitchTimeshiftModeOff();
            onResetTimeshiftNotification();
            onResetTimeshiftNotificationShown();
            setPlayerPaused(false);
            setPlayerPausedByUser(false);

            if (
                videoItem.endTime &&
                isPast(secondsToMilliseconds(videoItem.endTime))
            ) {
                const liveProgram = await onGetLiveProgram(
                    channel.editorial.id,
                );

                onSwitchPlayerSource(
                    parseVideoItem({ channel, program: liveProgram }),
                    StreamTypes.live,
                );
                onUpdateBookmark();
            } else {
                onSwitchPlayerSource(
                    parseVideoItem({
                        channel,
                        program: isRecordingItem(videoItem.originalProgram)
                            ? undefined
                            : videoItem.originalProgram,
                    }),
                    StreamTypes.live,
                );
            }
        }
    }, [
        channel,
        videoItem,
        onSwitchPlayerSource,
        onSwitchTimeshiftModeOff,
        onResetTimeshiftNotification,
        onResetTimeshiftNotificationShown,
        setPlayerPaused,
        setPlayerPausedByUser,
        onSetReadyToPlay,
        onGetLiveProgram,
        onUpdateBookmark,
    ]);
}

export default useJumpToLive;
